import type { PromptAnalysisCategory } from '@app/shared/prompt-definition/prompt-definition.component';

export const TOKEN_KEY = 'token';
export const DOWNLOAD_TOKEN_KEY = 'download_token';

export const API_PREFIX = '/api/v1';
// TODO use platform config for date format
export const DATE_FORMAT =
    'MM-DD-YYYY'; /* Date format to be used for displaying date on any view */
/**
 * Date format is set as year-month-day across the app, wherever date is input by user.
 * Different libraries interpret year-month-day differently.
 */
export const SAML_SSO_PATH = '/saml/sso';
export const SAML_SLO_PATH = '/saml/slo';

export const OAUTH_SSO_PATH = '/oauth2/sso';

// Date format used in Devex date component for date serialization
export const DX_DATE_FORMAT = 'yyyy-MM-dd';
// Date format to be used by Moment library
export const MOMENT_DATETIME_FORMAT = 'MM-DD-YYYY hh:mm A';
/**
 *  Moment datetime utc format
 * 'YYYY': Represents the full 4-digit year (e.g., 2023).
 'MM': Represents the 2-digit month (01 to 12).
 'DD': Represents the 2-digit day of the month (01 to 31).
 'T': A literal uppercase 'T' used as a separator between the date and time components.
 'hh': Represents the 2-digit hour in a 12-hour clock format with leading zeros (01 to 12).
 'mm': Represents the 2-digit minute with leading zeros (00 to 59).
 'ss': Represents the 2-digit seconds with leading zeros (00 to 59).
 'SSSS': Represents the any number of digits in milliseconds.
 */
export const MOMENT_UTC_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSS';
export const VALIDATION_UTC_DATETIME_FORMAT_DXDATEBOX = 'YYYY-MM-DDTHH:mm:ss';
export const DATE_TIME_FORMAT_TO_VALIDATE = 'YYYY-MM-DD HH:mm:SS';
export const DATE_FORMAT_TO_VALIDATE = 'YYYY-MM-DD';

// used to provide dateSerializationFormat in dx-date-box
export const UTC_DATETIME_FORMAT_FOR_DXDATEBOX = 'yyyy-MM-ddTHH:mm:ssZ';

export const TOAST_DEFAULT_TIMEOUT = 3000;

// Top level modules
export enum MODULES {
    ASSETS = 'Assets',
    TESTING = 'Testing',
    MANAGEMENT = 'Management',
    MONITOR = 'Monitor',
}

export const MODULE_DISPLAY_NAME: { [key in MODULES]: string } = {
    [MODULES.ASSETS]: 'Data & AI Assets',
    [MODULES.TESTING]: 'Human Integrated Testing',
    [MODULES.MANAGEMENT]: 'Risk & Compliance',
    [MODULES.MONITOR]: 'Monitor & Track',
};

export const MODULE_DISPLAY_DESCRIPTION: { [key in MODULES]: string } = {
    [MODULES.ASSETS]: 'Organize your Data, AI, Evaluation assets to power your GenAI usecases',
    [MODULES.TESTING]: 'Run structured evaluations and human review workflows for your pipelines',
    [MODULES.MANAGEMENT]:
        'Identify, Measure, and Mitigate risk and streamline compliance processes',
    [MODULES.MONITOR]: 'Track performance and ensure reliability with continuous monitoring',
};

export enum PAGES {
    APPROVALS = 'Approvals',
    DATASETS = 'Datasets',
    NOTEBOOKS = 'Notebooks',
    REPORTS = 'Reports',
    GLOBAL_FUNCTIONS = 'GlobalFunctions',
    USERS = 'Users',
    TEAMS = 'Teams',
    ROLES = 'Roles',
    PROJECT_MEMBERS = 'MembersAssignment',
    PROJECT_SETTINGS = 'ProjectSettings',
    APPROVAL_WORKFLOWS = 'ApprovalWorkflows',
    PLATFORM_SETTINGS = 'PlatformSettings',
    MODEL_CATALOG = 'ModelCatalog',
    PROMPT_REGISTRY = 'PromptRegistry',
    RAG_REGISTRY = 'RagRegistry',
    PIPELINE_BUILDER = 'PipelineBuilder',
    CODE_REPOS = 'CodeRepos',
    EVALUATIONS = 'Evaluations',
    FEEDBACK_PORTALS = 'FeedbackPortals',
    ANNOTATION_QUEUE = 'AnnotationQueues',
    MONITORING = 'Monitoring',
    RISK_ASSESSMENTS = 'RiskAssessments',
    DEPLOYMENTS = 'Deployments',
}

// Sub pages in the modules
// NOTE: The ordering of pages is used by some components - so keep it same as in the UI. For example:
//       - In user-object-access.component to show the pages in the ProjectRole page
//       - In default-sub-menu-page.guard to find the first accessible page in a module
export const MODULE_PAGES_MAP: { readonly [key in MODULES]: readonly PAGES[] } = {
    [MODULES.ASSETS]: [
        PAGES.DATASETS,
        PAGES.MODEL_CATALOG,
        PAGES.PROMPT_REGISTRY,
        PAGES.RAG_REGISTRY,
        PAGES.PIPELINE_BUILDER,
        PAGES.CODE_REPOS,
        PAGES.GLOBAL_FUNCTIONS,
        PAGES.REPORTS,
    ],
    [MODULES.TESTING]: [PAGES.EVALUATIONS, PAGES.FEEDBACK_PORTALS],
    [MODULES.MANAGEMENT]: [PAGES.RISK_ASSESSMENTS, PAGES.APPROVALS, PAGES.DEPLOYMENTS],
    [MODULES.MONITOR]: [PAGES.MONITORING, PAGES.ANNOTATION_QUEUE],
} as const;

// Pages which are hidden by default for a module
export const HIDDEN_MODULE_PAGES_MAP: { readonly [key in MODULES]?: readonly PAGES[] } = {
    [MODULES.ASSETS]: [
        PAGES.MODEL_CATALOG,
        PAGES.PROMPT_REGISTRY,
        PAGES.RAG_REGISTRY,
        PAGES.CODE_REPOS,
        PAGES.GLOBAL_FUNCTIONS,
    ],
} as const;

export enum OBJECTS {
    APPROVAL_WORKFLOW = 'ApprovalWorkflow',
    DATA_TABLE = 'DataTable',
    GLOBAL_FUNCTION = 'GlobalFunction',
    REPORT = 'Report',
    GROUP = 'Group',
    USER = 'User',
    // Keep the persisted/API object value stable while using the project-role name in code.
    PROJECT_ROLE = 'UserRole',
    PLATFORM_SETTING = 'PlatformSetting',
    FOUNDATION_MODEL = 'FoundationModel',
    RAG = 'Rag',
    PROMPT = 'Prompt',
    PIPELINE = 'Pipeline',
    CODE_REPO = 'CodeRepo',
    FEEDBACK_PORTAL = 'FeedbackPortal',
    TEST_SESSION = 'TestSession',
    ANNOTATION_QUEUE = 'AnnotationQueue',
    MONITORING = 'Monitoring',
    RISK_ASSESSMENT = 'RiskAssessment',
    DEPLOYMENT = 'Deployment',
}

export const OBJECT_MODULE_MAP: Partial<Record<OBJECTS, MODULES>> = {
    [OBJECTS.DATA_TABLE]: MODULES.ASSETS,
    [OBJECTS.GLOBAL_FUNCTION]: MODULES.ASSETS,
    [OBJECTS.REPORT]: MODULES.ASSETS,
    [OBJECTS.PROMPT]: MODULES.ASSETS,
    [OBJECTS.FOUNDATION_MODEL]: MODULES.ASSETS,
    [OBJECTS.RAG]: MODULES.ASSETS,
    [OBJECTS.PIPELINE]: MODULES.ASSETS,
    [OBJECTS.CODE_REPO]: MODULES.ASSETS,
    [OBJECTS.FEEDBACK_PORTAL]: MODULES.TESTING,
    [OBJECTS.TEST_SESSION]: MODULES.TESTING,
    [OBJECTS.ANNOTATION_QUEUE]: MODULES.MONITOR,
    [OBJECTS.MONITORING]: MODULES.MONITOR,
    [OBJECTS.RISK_ASSESSMENT]: MODULES.MANAGEMENT,
    [OBJECTS.DEPLOYMENT]: MODULES.MANAGEMENT,
};

// This is a mapping of which object gets created in which page - so, only for registries
export const PAGE_OBJECT_MAP: { [key in PAGES]?: OBJECTS } = {
    [PAGES.DATASETS]: OBJECTS.DATA_TABLE,
    [PAGES.GLOBAL_FUNCTIONS]: OBJECTS.GLOBAL_FUNCTION,
    [PAGES.USERS]: OBJECTS.USER,
    [PAGES.ROLES]: OBJECTS.PROJECT_ROLE,
    [PAGES.APPROVAL_WORKFLOWS]: OBJECTS.APPROVAL_WORKFLOW,
    [PAGES.REPORTS]: OBJECTS.REPORT,
    [PAGES.PLATFORM_SETTINGS]: OBJECTS.PLATFORM_SETTING,
    [PAGES.MODEL_CATALOG]: OBJECTS.FOUNDATION_MODEL,
    [PAGES.RAG_REGISTRY]: OBJECTS.RAG,
    [PAGES.PROMPT_REGISTRY]: OBJECTS.PROMPT,
    [PAGES.PIPELINE_BUILDER]: OBJECTS.PIPELINE,
    [PAGES.CODE_REPOS]: OBJECTS.CODE_REPO,
    [PAGES.EVALUATIONS]: OBJECTS.TEST_SESSION,
    [PAGES.FEEDBACK_PORTALS]: OBJECTS.FEEDBACK_PORTAL,
    [PAGES.ANNOTATION_QUEUE]: OBJECTS.ANNOTATION_QUEUE,
    [PAGES.MONITORING]: OBJECTS.MONITORING,
    [PAGES.RISK_ASSESSMENTS]: OBJECTS.RISK_ASSESSMENT,
    [PAGES.DEPLOYMENTS]: OBJECTS.DEPLOYMENT,
};

export const PROJECTABLE_PAGES: PAGES[] = [
    PAGES.DATASETS,
    PAGES.MODEL_CATALOG,
    PAGES.RAG_REGISTRY,
    PAGES.PROMPT_REGISTRY,
    PAGES.PIPELINE_BUILDER,
    PAGES.CODE_REPOS,
    PAGES.GLOBAL_FUNCTIONS,
    PAGES.REPORTS,
    PAGES.EVALUATIONS,
    PAGES.FEEDBACK_PORTALS,
    PAGES.RISK_ASSESSMENTS,
    PAGES.APPROVALS,
    PAGES.DEPLOYMENTS,
    PAGES.MONITORING,
    PAGES.ANNOTATION_QUEUE,
    PAGES.PROJECT_SETTINGS,
    PAGES.ROLES, // This is ProjectRole
    PAGES.PROJECT_MEMBERS,
];

export enum ACTIONS {
    VISIBLE = 'Visible',
    READ = 'Read',
    WRITE = 'Write',
    APPROVE = 'Approve',
}

export enum AUDIT_ACTION {
    CREATED = 'Created',
    STATUS_CHANGED = 'Status Changed',
    CHANGED = 'Changed',
    DELETED = 'Deleted',
    COMMENTED = 'Commented',
}

export enum FEATURE_TYPE {
    NUMERICAL = 'float',
    INTEGER = 'int',
    STRING = 'str',
    BOOLEAN = 'bool',
    ARRAY_NUMERICAL = 'list[float]',
    ARRAY_INTEGER = 'list[int]',
    ARRAY_STRING = 'list[str]',
    ARRAY_BOOLEAN = 'list[bool]',
}

export enum SCHEDULED_JOB_STATUS {
    COMPILING = 'COMPILING',
    QUEUED = 'QUEUED',
    RUNNING = 'RUNNING',
    SCHEDULED = 'SCHEDULED',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    STOPPING = 'STOPPING',
    STOPPED = 'STOPPED',
}

export enum SIMULTATION_TIME {
    COMPILE = 'compile',
    QUEUE = 'queue',
    DATA_CREATION = 'data_creation',
    EXECUTION = 'execution',
    REPORT = 'report',
}

export enum JOB_STEP_STATUS {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
    SUCCESSFUL = 'SUCCESSFUL',
    FAILED = 'FAILED',
}

export enum NOTIFICATION_TYPES {
    SUCCESS = 'Success',
    FAILURE = 'Failure',
    INFO = 'Info',
}

// This status will be used everywhere except policy as it has some extra statuses
export enum WORKFLOW_STATUS {
    DRAFT = 'Draft',
    PENDING_APPROVAL = 'Pending Approval',
    APPROVED = 'Approved',
    REJECTED = 'Rejected',
    ARCHIVED = 'Archived',
    ACTIVE = 'Active',
}

export enum EVALUATION_METHODS_TYPE {
    LLM_AS_JUDGE = 'llm-as-judge',
    HEURISTIC = 'heuristic',
    ML_BASED = 'statistical',
    OTHER = 'other',
}

export const EVALUATION_METHODS: {
    [key in EVALUATION_METHODS_TYPE]: { displayName: string; description: string };
} = {
    [EVALUATION_METHODS_TYPE.LLM_AS_JUDGE]: {
        displayName: 'LLM-as-a-Judge',
        description: 'Uses a Large Language Model for assessment.',
    },
    [EVALUATION_METHODS_TYPE.ML_BASED]: {
        displayName: 'NLP / ML Algorithms',
        description: 'Uses quantitative metrics or auxiliary models for evaluation.',
    },
    [EVALUATION_METHODS_TYPE.HEURISTIC]: {
        displayName: 'Rule-based',
        description: 'Relies on predefined rules or patterns to evaluate responses.',
    },
    [EVALUATION_METHODS_TYPE.OTHER]: {
        displayName: 'Others',
        description: 'Covers evaluation methodologies not listed above.',
    },
};

export enum INPUT_CASES {
    // For the list of Elements that can be used as a proxy for Elements that need a proxy
    PROXY_FEATURE_INPUTS = 'proxy_feature_inputs',
    // For list of available global functions & RTP for global function
    GLOBAL_FUNCTION_INPUTS = 'global_function_inputs',
    // For list of available global functionsfor reports
    REPORT_INPUTS = 'report_inputs',
    // Lists of input for report data source
    REPORT_DATA_SOURCE_INPUTS = 'report_data_source_inputs',
    // For list of available reports for while running a simulation.
    SIMULATION_REPORT_INPUTS = 'simulation_report_inputs',
    // For list of global functions in algorithm
    ALGORITHM_INPUTS = 'algorithm_inputs',
    // For list of entities based on entity_type from which to choose a job
    REUSE_SIMULATION_ENTITIES = 'reuse_simulation_entities',
    // For list of simulations based on entity and entity_type that can be reused for a job
    REUSE_SIMULATIONS = 'reuse_simulations',
    // For list of dashboard tabs in run-job details page
    FOUNDATION_MODEL_INPUTS = 'foundation_model_inputs',
    //  For list of inputs populated for Rag Details
    RAG_INPUTS = 'rag_inputs',
    //  For list of inputs populated for Pipeline Details
    PIPELINE_INPUTS = 'pipeline_inputs',
    // For the list of available models to use for comparison when doing a Run Simulation
    PIPELINE_COMPARISON_INPUTS = 'pipeline_comparison_inputs',
    // For list of inputs that be used for data logic to create datasource when running simulation for object
    // without platform entities like foundation_model and rag
    DATA_LOGIC_INPUTS = 'data_logic_inputs',
    // For the list of available model to use for comparison when doing a Run Simulation
    FOUNDATION_MODEL_COMPARISON_INPUTS = 'foundation_model_comparison_inputs',
    // For the list of available rags to use for comparison when doing a Run Simulation
    RAG_COMPARISON_INPUTS = 'rag_comparison_inputs',
    // Prompt Inputs
    PROMPT_INPUTS = 'prompt_inputs',
    // Prompt Comparison
    PROMPT_COMPARISON_INPUTS = 'prompt_comparison_inputs',
    // For the list of pipelines in Monitor > Monitoring
    MONITORING_PIPELINE_INPUTS = 'monitoring_pipeline_inputs',
    // For list of data tables to be used in annotation queue data ingestion
    ANNOTATION_QUEUE_DATA_TABLE_INPUTS = 'annotation_queue_data_table_inputs',
    // For the list of pipelines in Testing > Feedback portal
    FEEDBACK_PORTAL_PIPELINE_INPUTS = 'feedback_portal_pipeline_inputs',
    // For list of available feedback portals to use in test session
    TEST_SESSION_FEEDBACK_PORTAL_INPUTS = 'test_session_feedback_portal_inputs',
    // For the list of pipelines in Management > Risk Assessment
    RISK_ASSESSMENT_PIPELINE_INPUTS = 'risk_assessment_pipeline_inputs',
    // For list of users who can review an object
    OBJECT_REVIEWERS = 'object_reviewers',
}

export enum LINEAGE_CASES {
    PARENTS = 'parents',
    CHILDREN = 'children',
}

export enum JOB_SAMPLE_TYPES {
    SAMPLE_SIZE = 'Sample Size',
    SAMPLE_RATIO = 'Sample Ratio',
    FULL = 'Full',
}

export enum MODULE_ROUTE_PATH {
    // Project related modules:
    ASSETS = 'assets',
    TESTING = 'testing',
    MANAGEMENT = 'management',
    MONITOR = 'monitor',
    // These are non-project related modules:
    // NOTE: /profile/my-account is hardcoded in ggx-sdk. Update there is changing here
    SETTING = 'settings',
    PROFILE = 'profile',
    HOME = 'home',
}

export const MODULE_ROUTE_MAP: { [key in MODULES]: MODULE_ROUTE_PATH } = {
    [MODULES.ASSETS]: MODULE_ROUTE_PATH.ASSETS,
    [MODULES.TESTING]: MODULE_ROUTE_PATH.TESTING,
    [MODULES.MANAGEMENT]: MODULE_ROUTE_PATH.MANAGEMENT,
    [MODULES.MONITOR]: MODULE_ROUTE_PATH.MONITOR,
} as const;

export enum PAGE_ROUTE_PATH {
    DATASETS = 'datasets',
    ALGORITHMS = 'algorithms',
    APPROVALS = 'approvals',
    VALIDATION = 'validations',
    FEATURES = 'features',
    GLOBAL_FUNCTIONS = 'global-functions',
    ROLE_MANAGEMENT = 'role-management',
    PROJECT_MEMBERS = 'project-member',
    PROJECT_SETTINGS = 'settings',
    PYTHON_PACKAGES = 'python-packages',
    PROJECT_INTEGRATIONS = 'integrations',
    PROJECT_ADVANCED = 'advanced',
    USER_MANAGEMENT = 'user-management',
    TEAMS = 'teams',
    APPROVAL_WORKFLOWS = 'approval-workflow',
    GROUPS = 'groups',
    REPORT = 'report',
    PLATFORM_SETTINGS = 'platform-settings',
    MODEL_CATALOG = 'model-catalog',
    PROMPT_REGISTRY = 'prompt-registry',
    RAG_REGISTRY = 'rag-registry',
    PIPELINE_BUILDER = 'pipeline-builder',
    CODE_REPOS = 'code',
    EVALUATIONS = 'evaluations',
    FEEDBACK_PORTALS = 'feedback-portals',
    ANNOTATION_QUEUE = 'annotation-queue',
    MONITORING = 'monitoring',
    RISK_ASSESSMENTS = 'risk-assessments',
    DEPLOYMENTS = 'deployments',
}

export const PAGE_ROUTE_MAP: { [key in PAGES]?: PAGE_ROUTE_PATH } = {
    [PAGES.APPROVALS]: PAGE_ROUTE_PATH.APPROVALS,
    [PAGES.DATASETS]: PAGE_ROUTE_PATH.DATASETS,
    [PAGES.GLOBAL_FUNCTIONS]: PAGE_ROUTE_PATH.GLOBAL_FUNCTIONS,
    [PAGES.USERS]: PAGE_ROUTE_PATH.USER_MANAGEMENT,
    [PAGES.TEAMS]: PAGE_ROUTE_PATH.TEAMS,
    [PAGES.ROLES]: PAGE_ROUTE_PATH.ROLE_MANAGEMENT,
    [PAGES.PROJECT_MEMBERS]: PAGE_ROUTE_PATH.PROJECT_MEMBERS,
    [PAGES.PROJECT_SETTINGS]: PAGE_ROUTE_PATH.PROJECT_SETTINGS,
    [PAGES.APPROVAL_WORKFLOWS]: PAGE_ROUTE_PATH.APPROVAL_WORKFLOWS,
    [PAGES.REPORTS]: PAGE_ROUTE_PATH.REPORT,
    [PAGES.PLATFORM_SETTINGS]: PAGE_ROUTE_PATH.PLATFORM_SETTINGS,
    [PAGES.MODEL_CATALOG]: PAGE_ROUTE_PATH.MODEL_CATALOG,
    [PAGES.PROMPT_REGISTRY]: PAGE_ROUTE_PATH.PROMPT_REGISTRY,
    [PAGES.RAG_REGISTRY]: PAGE_ROUTE_PATH.RAG_REGISTRY,
    [PAGES.PIPELINE_BUILDER]: PAGE_ROUTE_PATH.PIPELINE_BUILDER,
    [PAGES.CODE_REPOS]: PAGE_ROUTE_PATH.CODE_REPOS,
    [PAGES.EVALUATIONS]: PAGE_ROUTE_PATH.EVALUATIONS,
    [PAGES.FEEDBACK_PORTALS]: PAGE_ROUTE_PATH.FEEDBACK_PORTALS,
    [PAGES.ANNOTATION_QUEUE]: PAGE_ROUTE_PATH.ANNOTATION_QUEUE,
    [PAGES.MONITORING]: PAGE_ROUTE_PATH.MONITORING,
    [PAGES.RISK_ASSESSMENTS]: PAGE_ROUTE_PATH.RISK_ASSESSMENTS,
    [PAGES.DEPLOYMENTS]: PAGE_ROUTE_PATH.DEPLOYMENTS,
} as const;

export enum MODEL_INPUT_TYPE {
    PYTHON_FUNCTION = 'python function',
    API_BASED = 'api-based',
    CODE_REPO = 'code-repo',
    DATASOURCE = 'datasource',
}

export enum UPLOAD_TYPES {
    FILE_UPLOAD = 'file-upload',
    MANUAL = 'manual',
}

export enum DATA_SOURCE_CONNECTION_TYPES {
    S3 = 's3',
    GCS = 'gcs',
    ABS = 'abs',
    HDFS = 'hdfs',
    OTHER_FS = 'other_fs',
    SNOWFLAKE = 'snowflake',
    HIVE = 'hive',
    MSSQL = 'mssql',
    ORACLE = 'oracle',
    JDBC = 'jdbc',
    DATABRICKS = 'databricks',
    UPLOAD = 'upload',
    CUSTOM_LOGIC = 'custom_logic',
}

export enum EXECUTION_TYPE {
    // The order here is how it shows up in the 'Run' dropdown
    SIMULATION = 'Simulation',
    HILL_CLIMBING = 'HillClimbing',
    COMPARISON = 'Comparison',
    MONITORING = 'Monitoring',
    INGESTION = 'Ingestion',
}

export const EXECUTION_ICON: { [key in EXECUTION_TYPE]: string } = {
    [EXECUTION_TYPE.SIMULATION]: 'pi pi-chart-bar',
    [EXECUTION_TYPE.HILL_CLIMBING]: 'pi pi-chart-line',
    [EXECUTION_TYPE.COMPARISON]: 'pi pi-arrow-right-arrow-left',
    [EXECUTION_TYPE.MONITORING]: 'pi pi-wave-pulse',
    [EXECUTION_TYPE.INGESTION]: 'pi pi-database',
};

export enum STATUS_ALIAS {
    APPROVED = 'A',
    REJECTED = 'R',
    PENDING_APPROVAL = 'P',
    DRAFT = 'D',
    ARCHIVED = 'AR', // FIXME: Need a better alias here ?
    ACTIVE = 'Active',
}

export const STATUS_COLOR_CODE: { [key in WORKFLOW_STATUS]: string } = {
    [WORKFLOW_STATUS.DRAFT]: 'cor--status-draft-tag',
    [WORKFLOW_STATUS.PENDING_APPROVAL]: 'cor--status-pending-tag',
    [WORKFLOW_STATUS.APPROVED]: 'cor--status-approved-tag',
    [WORKFLOW_STATUS.REJECTED]: 'cor--status-rejected-tag',
    [WORKFLOW_STATUS.ARCHIVED]: 'cor--status-archived-tag',
    [WORKFLOW_STATUS.ACTIVE]: 'cor--status-approved-tag',
};

export const STATUS_SHORT_CODE: { [key in WORKFLOW_STATUS]: STATUS_ALIAS } = {
    [WORKFLOW_STATUS.DRAFT]: STATUS_ALIAS.DRAFT,
    [WORKFLOW_STATUS.PENDING_APPROVAL]: STATUS_ALIAS.PENDING_APPROVAL,
    [WORKFLOW_STATUS.APPROVED]: STATUS_ALIAS.APPROVED,
    [WORKFLOW_STATUS.REJECTED]: STATUS_ALIAS.REJECTED,
    [WORKFLOW_STATUS.ARCHIVED]: STATUS_ALIAS.ARCHIVED,
    [WORKFLOW_STATUS.ACTIVE]: STATUS_ALIAS.ACTIVE,
};

export const EXECUTION_TYPE_SHORT_CODE_MAPPER: { [key in EXECUTION_TYPE]: string } = {
    [EXECUTION_TYPE.SIMULATION]: 'S',
    [EXECUTION_TYPE.HILL_CLIMBING]: 'HC',
    [EXECUTION_TYPE.COMPARISON]: 'C',
    [EXECUTION_TYPE.MONITORING]: 'Mo',
    [EXECUTION_TYPE.INGESTION]: 'I',
};

export enum REVIEW_STATUS {
    PENDING_ACCEPTANCE = 'Pending Acceptance',
    NEED_INFO = 'Need Info',
    NEED_CHANGES = 'Need Changes',
    CANCELED = 'Canceled',
    ACCEPTED = 'Accepted',
    ADD_COMMENT = 'Add Comment',
    REJECTED = 'Rejected',
}

export const OK_RESPONSE = 'OK';

export const SIMULATABLE_OBJECTS: readonly OBJECTS[] = [
    OBJECTS.PIPELINE,
    OBJECTS.FOUNDATION_MODEL,
    OBJECTS.RAG,
    // OBJECTS.PROMPT,  // Removing for now. As going forward, prompts will be strings. Need to rethink how to test them
    OBJECTS.GLOBAL_FUNCTION,
    OBJECTS.MONITORING,
    OBJECTS.REPORT,
];

export const GROUPABLE_OBJECTS = [
    OBJECTS.DATA_TABLE,
    OBJECTS.PROMPT,
    OBJECTS.FOUNDATION_MODEL,
    OBJECTS.RAG,
    OBJECTS.PIPELINE,
    OBJECTS.GLOBAL_FUNCTION,
    OBJECTS.REPORT,
    OBJECTS.ANNOTATION_QUEUE,
    OBJECTS.MONITORING,
    OBJECTS.FEEDBACK_PORTAL,
];

export const WORKFLOWABLE_OBJECTS: readonly OBJECTS[] = [
    OBJECTS.GLOBAL_FUNCTION,
    OBJECTS.REPORT,
    OBJECTS.FOUNDATION_MODEL,
    OBJECTS.PROMPT,
    OBJECTS.RAG,
    OBJECTS.PIPELINE,
];

export const SYSTEM_APPROVALS = {
    WORKFLOW_RULE: 'Workflow Rule',
    ALL: 'All',
};

export enum REPORT_OUTPUT_TYPES {
    FIGURE = 'Figure',
    METRIC = 'Metric',
}

//NOTE: any changes made here sync with the json schema for REPORT_DYNAMIC_FIELD_SCHEMA
export enum REPORT_PARAMETER_TYPE {
    NUMBERBOX = 'numberbox',
    TEXTBOX = 'textbox',
    SELECTBOX = 'selectbox',
    SELECTBUTTON = 'selectbutton',
    SINGLE_OBJECT = 'SingleObject', // This is a hack - adding for Data Schema columns.
}

export const REPORT_PARAMETER_TYPES_LIST: { type: REPORT_PARAMETER_TYPE; name: string }[] = [
    { type: REPORT_PARAMETER_TYPE.NUMBERBOX, name: 'Number' },
    // Make these names clear for GenAI and remove multiple objects as we do not support it in GenAI
    { type: REPORT_PARAMETER_TYPE.SINGLE_OBJECT, name: 'String Column' },
];

export enum ALGORITHM_PARAMETER_TYPES {
    STRING = 'String',
    NUMBER = 'Number',
}

// NOTE: This is the order in which the values are shown to users
export const EXECUTION_INFO: {
    [key in EXECUTION_TYPE]: {
        name: string;
        tooltip: string;
    };
} = {
    [EXECUTION_TYPE.SIMULATION]: {
        name: 'Simulation',
        tooltip: 'Run on a batch of data and generate dashboards on the output',
    },
    [EXECUTION_TYPE.HILL_CLIMBING]: {
        name: 'Hillclimb',
        tooltip:
            'Try changes to the prompts or the model of your agent, one at a time, to find the version that works best',
    },
    [EXECUTION_TYPE.COMPARISON]: {
        name: 'Comparison',
        tooltip: 'Compare the results of two completed jobs side by side',
    },
    [EXECUTION_TYPE.MONITORING]: {
        name: 'Monitoring Job',
        tooltip:
            'Process a data feed and generate dashboards to monitor trends in the pipeline results',
    },
    [EXECUTION_TYPE.INGESTION]: {
        name: 'Injestion Job',
        tooltip: 'Ingest data from a data source into the platform for use in annotation queue',
    },
};

export enum FIELD_TYPE {
    SHORT_TEXT = 'ShortText',
    LONG_TEXT = 'LongText',
    SINGLE_SELECT = 'SingleSelect',
    MULTI_SELECT = 'MultiSelect',
    SINGLE_FILE = 'SingleFile',
    DATETIME = 'DateTime',
    NUMBER = 'Number',
}

export const GGX_GLOBAL_OBJECTS = [
    OBJECTS.FOUNDATION_MODEL,
    OBJECTS.RAG,
    OBJECTS.PROMPT,
    OBJECTS.PIPELINE,
    OBJECTS.GLOBAL_FUNCTION,
    OBJECTS.MONITORING,
    OBJECTS.REPORT,
];

export const REGEX_UTILS = {
    VALID_SNAKE_CASE: /^[a-z][a-z0-9_]*$/,
    VALID_VARIABLE_NAME: /^[a-zA-Z_][a-z0-9A-Z_]*$/,
    VALID_COMMA_SEPARATED_VALUE: /^(?:[\w\s]+,)*[\w\s]+$/,
    VALID_EXTENSION: /\.[a-zA-Z0-9]+$/,
    VALID_EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
};

export enum APPROVAL_TYPE {
    GROUPED = 'grouped',
    INDIVIDUAL = 'individual',
}

export const APPROVAL_REVIEW_MAP: {
    readonly [key: string]: {
        readonly name: string;
        readonly action: REVIEW_STATUS | WORKFLOW_STATUS;
    };
} = {
    ACCEPT: { name: 'Accept', action: REVIEW_STATUS.ACCEPTED },
    NEED_INFO: { name: 'Need Info', action: REVIEW_STATUS.NEED_INFO },
    NEED_CHANGES: { name: 'Need Change', action: REVIEW_STATUS.NEED_CHANGES },
    REJECT: { name: 'Reject', action: WORKFLOW_STATUS.REJECTED },
    NOTIFY_REVIEWER: { name: 'Notify Reviewer', action: null },
    SEND_EMAIL_REMINDER: { name: 'Send Email Reminder', action: null },
    CANCEL_REQUEST: { name: 'Cancel', action: REVIEW_STATUS.CANCELED },
    RE_SUBMIT: { name: 'Resubmit', action: REVIEW_STATUS.PENDING_ACCEPTANCE },
    ADD_COMMENT: { name: 'Add Comment', action: REVIEW_STATUS.ADD_COMMENT },
};

export enum SKELETON_TYPES {
    LIST = 'list',
    JOB_LIST = 'job-list',
    DETAILS = 'details',
    SM_DETAILS = 'sm-details',
    BASE_DETAILS = 'base-details',
    DASHBOARD = 'dashboard',
    DASHBOARD_BASE = 'dashboard-base',
    ACCORDION = 'accordion',
    ROLE_DETAILS = 'role-details',
    GRID = 'grid',
    HISTORY = 'history',
    RUN_JOB_DETAILS = 'run-job-details',
    RAW_DATA_PULL = 'raw-data-pull',
    SCREEN_FIELDS = 'screen-fields',
    TAB_TITLE = 'tab-title',
    TEXT = 'text',
    JOB_INFORMATION = 'job-information',
    CHAT = 'chat',
}
export enum APPROVAL_WORKFLOW_STATUS {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
}

export const PAGE_INFO: {
    [key in PAGES]?: { name: string; tooltip?: string; icon?: string };
} = {
    [PAGES.DATASETS]: {
        name: 'Datasets',
        tooltip: 'Track your datasets and use them for various experiments and analytics',
        icon: 'pi pi-table',
    },
    [PAGES.APPROVALS]: {
        name: 'Approvals',
        tooltip: 'Review approval requests and completed decisions across workflowable objects',
        icon: 'pi pi-check-circle',
    },
    [PAGES.GLOBAL_FUNCTIONS]: {
        name: 'Global Functions',
        tooltip: 'Reusable helper functions to help with modularity',
        icon: 'pi pi-code',
    },
    [PAGES.USERS]: { name: 'Users', icon: 'pi pi-users' },
    [PAGES.TEAMS]: {
        name: 'Teams',
        tooltip: 'Organize users into teams and manage permissions for the group',
        icon: 'pi pi-user-plus',
    },
    [PAGES.ROLES]: {
        name: 'Project Roles',
        tooltip: 'Reusable roles defined to assign permissions to users/teams within the project',
        icon: 'pi pi-id-card',
    },
    [PAGES.PROJECT_MEMBERS]: {
        name: 'Project Members',
        tooltip: 'Manage which users belong to this project and which roles they have',
        icon: 'pi pi-users',
    },
    [PAGES.PROJECT_SETTINGS]: { name: 'Project Settings', icon: 'pi pi-cog' },
    [PAGES.APPROVAL_WORKFLOWS]: { name: 'Approval Workflows', icon: 'pi pi-check-circle' },
    [PAGES.REPORTS]: {
        name: 'Reports',
        tooltip:
            'Create reusable reports and metrics to analyze and evaluate your GenAI systems and data',
        icon: 'pi pi-chart-scatter',
    },
    [PAGES.PLATFORM_SETTINGS]: { name: 'Platform Integrations', icon: 'pi pi-sliders-h' },
    [PAGES.PROMPT_REGISTRY]: {
        name: 'Skills / Prompts',
        tooltip: 'Register re-usable Prompt Templates to use with LLMs',
        icon: 'pi pi-align-left',
    },
    [PAGES.MODEL_CATALOG]: {
        name: 'Models',
        tooltip:
            'Register reusable Models like LLMs, Foundational Models, Fine tuned models, Guardrail Models, etc.',
        icon: 'pi pi-microchip-ai',
    },
    [PAGES.RAG_REGISTRY]: {
        name: 'RAGs / Knowledge Bases',
        tooltip: 'Register GenAI Agents, RAGs, and Knowledge Bases to use in your GenAI Pipeline',
        icon: 'pi pi-database',
    },
    [PAGES.CODE_REPOS]: {
        name: 'Code Repos',
        tooltip: 'Git repositories hosted here, holding the code your agents run',
        icon: 'pi pi-folder-open',
    },
    [PAGES.PIPELINE_BUILDER]: {
        name: 'Agents',
        tooltip:
            'Register Agents that use various Models, Prompts, RAGs, Sub-Agents to provide a end-to-end solution',
        icon: 'pi pi-sitemap',
    },
    [PAGES.EVALUATIONS]: {
        name: 'Evaluations',
        tooltip: 'View test sessions and batch jobs in one place',
        icon: 'pi pi-gauge',
    },
    [PAGES.FEEDBACK_PORTALS]: {
        name: 'Feedback Portals',
        tooltip: 'Collect Human Feedback from your teams on your AI agents',
        icon: 'pi pi-list-check',
    },
    [PAGES.ANNOTATION_QUEUE]: {
        name: 'Annotation Queues',
        tooltip: 'Add Human feedback to your production data',
        icon: 'pi pi-user-edit',
    },
    [PAGES.MONITORING]: {
        name: 'Monitoring',
        tooltip: 'Track and Monitor your production AI runs',
        icon: 'pi pi-chart-line',
    },
    [PAGES.RISK_ASSESSMENTS]: {
        name: 'Risk Assessments',
        tooltip: 'Mitigate Risks for your pipeline',
        icon: 'pi pi-shield',
    },
    [PAGES.DEPLOYMENTS]: {
        name: 'Deployments',
        tooltip: 'Document production endpoints, connection details, and expected outputs',
        icon: 'pi pi-cloud-upload',
    },
};

export const OBJECT_INFO: {
    [key in OBJECTS]: { name: string; icon?: string; color?: string; background?: string };
} = {
    [OBJECTS.APPROVAL_WORKFLOW]: {
        name: 'Approval Workflow',
        icon: PAGE_INFO[PAGES.APPROVAL_WORKFLOWS].icon,
    },
    [OBJECTS.DATA_TABLE]: {
        name: 'Data Table',
        icon: PAGE_INFO[PAGES.DATASETS].icon,
        color: 'var(--cor-data-table-color)',
        background: 'var(--cor-data-table-background)',
    },
    [OBJECTS.GLOBAL_FUNCTION]: {
        name: 'Global Function',
        icon: PAGE_INFO[PAGES.GLOBAL_FUNCTIONS].icon,
        color: 'var(--cor-global-function-color)',
        background: 'var(--cor-global-function-background)',
    },
    [OBJECTS.REPORT]: {
        name: 'Report',
        icon: PAGE_INFO[PAGES.REPORTS].icon,
        color: 'var(--cor-report-color)',
        background: 'var(--cor-report-background)',
    },
    [OBJECTS.GROUP]: {
        name: 'Group',
        icon: 'pi pi-folder',
    },
    [OBJECTS.USER]: {
        name: 'User',
        icon: PAGE_INFO[PAGES.USERS].icon,
    },
    [OBJECTS.PROJECT_ROLE]: {
        name: 'Role',
        icon: PAGE_INFO[PAGES.ROLES].icon,
    },
    [OBJECTS.PLATFORM_SETTING]: {
        name: 'Platform Integrations',
        icon: PAGE_INFO[PAGES.PLATFORM_SETTINGS].icon,
    },
    [OBJECTS.FOUNDATION_MODEL]: {
        name: 'Model',
        icon: PAGE_INFO[PAGES.MODEL_CATALOG].icon,
        color: 'var(--cor-foundation-model-color)',
        background: 'var(--cor-foundation-model-background)',
    },
    [OBJECTS.RAG]: {
        name: 'RAG',
        icon: PAGE_INFO[PAGES.RAG_REGISTRY].icon,
        color: 'var(--cor-rag-color)',
        background: 'var(--cor-rag-background)',
    },
    [OBJECTS.CODE_REPO]: {
        name: 'Code Repo',
        icon: PAGE_INFO[PAGES.CODE_REPOS].icon,
        color: 'var(--cor-code-repo-color)',
        background: 'var(--cor-code-repo-background)',
    },
    [OBJECTS.PROMPT]: {
        name: 'Prompt',
        icon: PAGE_INFO[PAGES.PROMPT_REGISTRY].icon,
        color: 'var(--cor-prompt-color)',
        background: 'var(--cor-prompt-background)',
    },
    [OBJECTS.PIPELINE]: {
        name: 'Pipeline',
        icon: PAGE_INFO[PAGES.PIPELINE_BUILDER].icon,
        color: 'var(--cor-pipeline-color)',
        background: 'var(--cor-pipeline-background)',
    },
    [OBJECTS.FEEDBACK_PORTAL]: {
        name: 'Feedback Portal',
        icon: PAGE_INFO[PAGES.FEEDBACK_PORTALS].icon,
        color: 'var(--cor-feedback-portal-color)',
        background: 'var(--cor-feedback-portal-background)',
    },
    [OBJECTS.TEST_SESSION]: {
        name: 'Test Session',
        icon: 'pi pi-comments',
        color: 'var(--cor-test-session-color)',
        background: 'var(--cor-test-session-background)',
    },
    [OBJECTS.ANNOTATION_QUEUE]: {
        name: 'Annotation Queue',
        icon: PAGE_INFO[PAGES.ANNOTATION_QUEUE].icon,
        color: 'var(--cor-annotation-queue-color)',
        background: 'var(--cor-annotation-queue-background)',
    },
    [OBJECTS.MONITORING]: {
        name: 'Monitoring',
        icon: PAGE_INFO[PAGES.MONITORING].icon,
        color: 'var(--cor-monitoring-color)',
        background: 'var(--cor-monitoring-background)',
    },
    [OBJECTS.RISK_ASSESSMENT]: {
        name: 'Risk Assessment',
        icon: PAGE_INFO[PAGES.RISK_ASSESSMENTS].icon,
        color: 'var(--cor-risk-assessment-color)',
        background: 'var(--cor-risk-assessment-background)',
    },
    [OBJECTS.DEPLOYMENT]: {
        name: 'Deployment',
        icon: PAGE_INFO[PAGES.DEPLOYMENTS].icon,
    },
};

export const MODULE_IMAGE_MAP: { [key in MODULES]?: string } = {
    [MODULES.ASSETS]: 'assets.svg',
    [MODULES.TESTING]: 'testing.svg',
    [MODULES.MANAGEMENT]: 'management.svg',
    [MODULES.MONITOR]: 'monitor.svg',
};

export const MODULE_ICON_MAP: { [key in MODULES]?: string } = {
    [MODULES.ASSETS]: 'saxCpuSettingBulk',
    [MODULES.TESTING]: 'saxMilkBulk',
    [MODULES.MANAGEMENT]: 'saxShieldBulk',
    [MODULES.MONITOR]: 'saxMicroscopeBulk',
};

export const PROMPT_CATEGORY_COLOR_MAP: { [key in PromptAnalysisCategory]: string } = {
    Goal: '#64748b', // slate-500
    Bias: '#a855f7', // purple-500
    Toxicity: '#ef4444', // red-500
    'Logical Progression and Coherence': '#ec4899', // pink-500
    'Examples Quality': '#6366f1', // indigo-500
    Clarity: '#a16207', // yellow-700
    Grammar: '#14b8a6', // teal-500
};
